import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  MinusCircle,
} from 'lucide-react';
import React, { useContext, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { INGESTION_SECTIONS } from '@/services/types/case-ingestion-types';

import { SECTION_LABELS, SECTION_TO_STEP_INDEX } from '../../constants';
import { CaseSetupMessagingContext } from '../../context/CaseSetupMessagingContext';
import { useUnifiedFlow } from '../../context/UnifiedFlowContext';
import { IngestionStepLayout } from '../../layout';
import { ReviewMessagingPanel } from './ReviewMessagingPanel';

/**
 * Review step - displays all tracked sections with their status.
 * Three states: Needs Review (null), Found, Not Available.
 */
export const ReviewStep: React.FC = () => {
  const { getSectionStatuses, canFinish, goToStep, state } = useUnifiedFlow();
  const { matterId } = state;

  // Only show messaging panel when the CaseSetupMessagingProvider is present
  // (i.e. the automated_introduction workflow is enabled for this matter).
  const messagingCtx = useContext(CaseSetupMessagingContext);
  const showMessagingPanel = !!messagingCtx;

  const sectionStatuses = getSectionStatuses();

  // Sections not yet reviewed (blocks completion)
  const needsReviewSections = useMemo(() => {
    return INGESTION_SECTIONS.filter(
      (section) => sectionStatuses[section] === null
    );
  }, [sectionStatuses]);

  // Sections marked as found
  const foundSections = useMemo(() => {
    return INGESTION_SECTIONS.filter(
      (section) => sectionStatuses[section] === 'found'
    );
  }, [sectionStatuses]);

  // Sections marked as not available
  const notAvailableSections = useMemo(() => {
    return INGESTION_SECTIONS.filter(
      (section) => sectionStatuses[section] === 'not_found'
    );
  }, [sectionStatuses]);

  return (
    <div className="flex gap-4 min-h-0">
      {/* Left column: Review checklist */}
      <div className="flex-1 min-w-0">
        <IngestionStepLayout
          title="Review"
          description={
            canFinish
              ? 'All sections reviewed'
              : `${needsReviewSections.length} section${needsReviewSections.length === 1 ? '' : 's'} need${needsReviewSections.length === 1 ? 's' : ''} review`
          }
        >
          <div className="space-y-3">
        {/* Success banner when all sections reviewed */}
        {canFinish && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
            <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <p className="text-xs font-medium text-green-700 dark:text-green-300">
              All sections reviewed. Click Finish to complete.
            </p>
          </div>
        )}

        {/* Needs Review - prominent amber styling */}
        {needsReviewSections.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Needs Review
            </h3>
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 overflow-hidden">
              {needsReviewSections.map((section, index) => (
                <div
                  key={section}
                  className={`flex items-center justify-between gap-2 p-2 ${
                    index > 0
                      ? 'border-t border-amber-200 dark:border-amber-800'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      {SECTION_LABELS[section]}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goToStep(SECTION_TO_STEP_INDEX[section])}
                    className="h-7 text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900"
                  >
                    Review
                    <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
              Review all sections above to complete ingestion
            </p>
          </div>
        )}

        {/* Found - subtle green styling */}
        {foundSections.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Found
            </h3>
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 overflow-hidden">
              {foundSections.map((section, index) => (
                <div
                  key={section}
                  className={`flex items-center justify-between gap-2 p-2 ${
                    index > 0
                      ? 'border-t border-green-200 dark:border-green-800'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-300">
                      {SECTION_LABELS[section]}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goToStep(SECTION_TO_STEP_INDEX[section])}
                    className="h-7 text-xs font-medium text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 hover:bg-green-100 dark:hover:bg-green-900"
                  >
                    Edit
                    <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not Available - muted gray styling */}
        {notAvailableSections.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Not Available
            </h3>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden">
              {notAvailableSections.map((section, index) => (
                <div
                  key={section}
                  className={`flex items-center justify-between gap-2 p-2 ${
                    index > 0
                      ? 'border-t border-zinc-200 dark:border-zinc-800'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <MinusCircle className="h-3.5 w-3.5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                      {SECTION_LABELS[section]}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goToStep(SECTION_TO_STEP_INDEX[section])}
                    className="h-7 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    Edit
                    <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </IngestionStepLayout>
      </div>

      {/* Right column: Messaging preview (only when automated_introduction is enabled) */}
      {matterId && showMessagingPanel && (
        <div className="w-[380px] flex-shrink-0 rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900">
          <ReviewMessagingPanel matterId={matterId} />
        </div>
      )}
    </div>
  );
};
